/**
 * Billing API
 * API class for all billing configuration endpoints
 */

import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import type {
  OperatorBillingConfig,
  UpsertOperatorBillingConfigRequest,
  ExcludedMachine,
  AddExcludedMachineRequest,
  ReimbursementFee,
  ReimbursementFeesFilters,
} from '../types/billing-types';

export class BillingApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers = await this.tokenManager.getAuthHeaders();
    return { 'Content-Type': 'application/json', ...headers };
  }

  // ── Operator Config ────────────────────────────────────────────────────────

  async getOperatorConfigs(): Promise<OperatorBillingConfig[]> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/operator-configs`,
      { method: 'GET', headers },
      'Billing',
      '/billing'
    );
    const data = await response.json();
    const list = Array.isArray(data) ? data : (data.items ?? data.Items ?? []);
    return list.map((item) => ({
      operatorId: item.operatorId ?? item.OperatorId,
      operatorName: item.operatorName ?? item.OperatorName ?? null,
      distributorId: item.distributorId ?? item.DistributorId ?? null,
      ocpiConnectorsThreshold: item.ocpiConnectorsThreshold ?? item.OcpiConnectorsThreshold ?? null,
      noMsfForDc: item.noMsfForDc ?? item.NoMsfForDc ?? false,
      isEnergyCsms: item.isEnergyCsms ?? item.IsEnergyCsms ?? false,
      isApp: item.isApp ?? item.IsApp ?? false,
    }));
  }

  async deleteOperatorConfig(operatorId: number): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/operator-config/${operatorId}`,
      { method: 'DELETE', headers },
      'Billing',
      '/billing'
    );
  }

  async getOperatorConfig(operatorId: number): Promise<OperatorBillingConfig | null> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/operator-config?operatorId=${operatorId}`,
      { method: 'GET', headers },
      'Billing',
      '/billing'
    );
    const text = await response.text();
    if (!text) return null;
    const data = JSON.parse(text);
    if (!data) return null;
    return {
      operatorId: data.operatorId ?? data.OperatorId,
      ocpiConnectorsThreshold: data.ocpiConnectorsThreshold ?? data.OcpiConnectorsThreshold ?? null,
      noMsfForDc: data.noMsfForDc ?? data.NoMsfForDc ?? false,
      isEnergyCsms: data.isEnergyCsms ?? data.IsEnergyCsms ?? false,
      isApp: data.isApp ?? data.IsApp ?? false,
    };
  }

  async insertOperatorConfig(request: UpsertOperatorBillingConfigRequest): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/operator-config`,
      { method: 'POST', headers, body: JSON.stringify(request) },
      'Billing',
      '/billing'
    );
  }

  async updateOperatorConfig(request: UpsertOperatorBillingConfigRequest): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/operator-config`,
      { method: 'PUT', headers, body: JSON.stringify(request) },
      'Billing',
      '/billing'
    );
  }

  // ── Excluded Machines ──────────────────────────────────────────────────────

  async getExcludedMachines(): Promise<ExcludedMachine[]> {
    const headers = await this.getAuthHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/excluded-machines`,
      { method: 'GET', headers },
      'Billing',
      '/billing'
    );
    const data = await response.json();
    const list = Array.isArray(data) ? data : (data.items ?? data.Items ?? []);
    return list.map((item) => ({
      machineId: item.machineId ?? item.MachineId,
      reason: item.reason ?? item.Reason ?? null,
      createdDt: item.createdDt ?? item.CreatedDt ?? null,
      createdByName: item.createdByName ?? item.CreatedByName ?? null,
      updatedDt: item.updatedDt ?? item.UpdatedDt ?? null,
      updatedByName: item.updatedByName ?? item.UpdatedByName ?? null,
    }));
  }

  async addExcludedMachine(request: AddExcludedMachineRequest): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/excluded-machines`,
      { method: 'POST', headers, body: JSON.stringify(request) },
      'Billing',
      '/billing'
    );
  }

  async updateExcludedMachine(machineId: number, reason: string | null): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/excluded-machines/${machineId}`,
      { method: 'PUT', headers, body: JSON.stringify({ reason }) },
      'Billing',
      '/billing'
    );
  }

  async deleteExcludedMachine(machineId: number): Promise<void> {
    const headers = await this.getAuthHeaders();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/excluded-machines/${machineId}`,
      { method: 'DELETE', headers },
      'Billing',
      '/billing'
    );
  }

  // ── Distributors ──────────────────────────────────────────────────────────

  async getDistributors(): Promise<Array<{ id: number; name: string }>> {
    const headers = await this.getAuthHeaders();
    try {
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        `${this.baseUrl}/actor/distributors`,
        { method: 'GET', headers },
        'Billing',
        '/actor/distributors'
      );
      const data = await response.json();
      const list: Array<{ id?: number; Id?: number; name?: string; Name?: string }> =
        data.distributors ?? data.Distributors ?? [];
      return list.map(d => ({ id: d.id ?? d.Id ?? 0, name: d.name ?? d.Name ?? '' }));
    } catch {
      return [];
    }
  }

  // ── Reimbursement Fees ─────────────────────────────────────────────────────

  async getReimbursementFees(filters: Partial<ReimbursementFeesFilters>): Promise<ReimbursementFee[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();
    if (filters.distributorId) params.set('distributorId', filters.distributorId);
    if (filters.operatorId) params.set('operatorId', filters.operatorId);
    if (filters.actorId) params.set('actorId', filters.actorId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/billing/reimbursement-fees${query}`,
      { method: 'GET', headers },
      'Billing',
      '/billing'
    );
    const data = await response.json();
    const list = Array.isArray(data) ? data : (data.items ?? data.Items ?? []);
    return list.map((item) => ({
      id: item.id ?? item.Id,
      invoiceStartDate: item.invoiceStartDate ?? item.InvoiceStartDate ?? null,
      invoiceEndDate: item.invoiceEndDate ?? item.InvoiceEndDate ?? null,
      distributorId: item.distributorId ?? item.DistributorId ?? null,
      actorId: item.actorId ?? item.ActorId ?? null,
      machineId: item.machineId ?? item.MachineId ?? null,
      feeTypeId: item.feeTypeId ?? item.FeeTypeId ?? null,
      count: item.count ?? item.Count ?? null,
      status: item.status ?? item.Status ?? null,
      sentDt: item.sentDt ?? item.SentDt ?? null,
      createdDt: item.createdDt ?? item.CreatedDt ?? null,
    }));
  }
}
